import _extends from '@babel/runtime-corejs3/helpers/extends';
import React__default, { useRef, useState, useEffect, useCallback, isValidElement } from 'react';
import classNames from 'classnames';
import menuStyles from './NewMenu.js';
import '../../utils/index.js';
import '../../hooks/index.js';
import './parts/index.js';
import useId from '../../hooks/useId/useId.js';
import { useMenuContext, MenuContextProvider } from './parts/MenuContext.js';
import useTranslation from '../../utils/Translation/useTranslation.js';
import useControllableState from '../../hooks/useControllableState/useControllableState.js';
import useLatest from '../../hooks/useLatest/useLatest.js';
import { useDidUpdate } from '../../hooks/useDidUpdate/useDidUpdate.js';
import wrapEvent from '../../utils/wrapEvent/wrapEvent.js';
import useClickOutsidePortals from '../../hooks/useClickOutsidePortals/useClickOutsidePortals.js';
import MenuItem from './parts/MenuItem.js';
import useScreenReaderAnnouncement from '../../hooks/useScreenReaderAnnouncement/useScreenReaderAnnouncement.js';
import mergeRefs from '../../utils/mergeRefs/mergeRefs.js';
import SubmenuAnchor from './parts/SubmenuAnchor.js';
import MenuAnchor from './parts/MenuAnchor.js';
import MenuSpinner from './parts/MenuSpinner.js';
import MenuSearchBox from './parts/MenuSearchBox.js';
import Popper from '../../utils/Popper/Popper.js';
import { SUBMENU_POPPER_OFFSET, FLYOUT_POPPER_OFFSET } from '../../utils/constants.js';
import ArrowNav from './parts/ArrowNav/ArrowNav.js';

const BaseMenu = /*#__PURE__*/React__default.forwardRef((_ref, ref) => {
  var _externalAnchor$props, _externalAnchor$props2, _useMenuContext, _externalAnchor$props3, _externalAnchor$props4, _externalAnchor$props5, _externalAnchor$props6, _externalAnchor$props7, _externalAnchor$props8, _externalAnchor$props9, _anchorRef$current;
  let {
    anchor: externalAnchor,
    'aria-label': externalAriaLabel,
    'aria-labelledby': externalAriaLabelledBy,
    children,
    className,
    flyoutClassName,
    defaultLabel,
    defaultValue = null,
    disabled,
    highlightedIndex: externalHighlightedIndex,
    id: externalId,
    label,
    maxHeight,
    menuType,
    onClick,
    onClickAway,
    onHighlightedIndexChange,
    onKeyDown,
    onIsOpenChange,
    onSelectedValueChange,
    isOpen: externalIsOpen,
    placement = 'bottom-start',
    portalTarget,
    searchable = false,
    isSearchFilteringDisabled = false,
    searchValue: externalSearchValue,
    onSearchValueChange: externalOnSearchValueChange,
    selectable = false,
    selectedValue: externalSelectedValue,
    unstable_isLoading = false,
    unstable_isMultiSelect = false,
    // isMultiSelect prop added 8/28/20
    isMultiSelect = unstable_isMultiSelect,
    zIndex = 1000,
    maxFlyoutWidth,
    isVirtualized = false,
    private_isClosedMenuOpenedOnAnchorClick = true,
    private_isFirstElementHighlightedOnUserActivatedOpen = true,
    ...rest
  } = _ref;
  const anchorId = useId((_externalAnchor$props = externalAnchor === null || externalAnchor === void 0 || (_externalAnchor$props2 = externalAnchor.props) === null || _externalAnchor$props2 === void 0 ? void 0 : _externalAnchor$props2.id) !== null && _externalAnchor$props !== void 0 ? _externalAnchor$props : externalId);
  const arrowNavId = "".concat(anchorId, "-menu");
  const parentCloseAndFocusAnchor = (_useMenuContext = useMenuContext()) === null || _useMenuContext === void 0 ? void 0 : _useMenuContext.closeAndFocusAnchor;
  const isSubmenu = Boolean(parentCloseAndFocusAnchor);
  const {
    translate
  } = useTranslation();
  const noMenuItemsToDisplayText = translate('menu.noItemsToDisplay');
  const anchorRef = useRef(null);
  const [isOpen, setIsOpen] = useControllableState(false, onIsOpenChange, externalIsOpen);
  const [highlightedIndex, setHighlightedIndex] = useControllableState(-1, onHighlightedIndexChange, externalHighlightedIndex);
  const [selectedValue, setSelectedValue] = useControllableState(defaultValue, onSelectedValueChange, externalSelectedValue);
  const [searchTerm, setSearchTerm] = useControllableState('', externalOnSearchValueChange, externalSearchValue);
  const [submenuHighlightedIndex, setSubmenuHighlightedIndex] = useState(-1);

  // These effects are needed for onIsOpenChange to work for submenus since the isOpen state is always controlled by the parent menu
  const onIsOpenChangeRef = useLatest(onIsOpenChange);
  const externalIsOpenRef = useLatest(externalIsOpen);
  // Call onIsOpenChange whenever externalIsOpen changes
  useDidUpdate(() => {
    if (isSubmenu) {
      var _onIsOpenChangeRef$cu;
      (_onIsOpenChangeRef$cu = onIsOpenChangeRef.current) === null || _onIsOpenChangeRef$cu === void 0 || _onIsOpenChangeRef$cu.call(onIsOpenChangeRef, externalIsOpen);
    }
  }, [externalIsOpen, onIsOpenChangeRef, isSubmenu]);
  // Call onIsOpenChange if open on mount or on unmount
  useEffect(() => {
    if (isSubmenu) {
      if (externalIsOpenRef.current) {
        var _onIsOpenChangeRef$cu2;
        (_onIsOpenChangeRef$cu2 = onIsOpenChangeRef.current) === null || _onIsOpenChangeRef$cu2 === void 0 || _onIsOpenChangeRef$cu2.call(onIsOpenChangeRef, true);
      }
      return () => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (externalIsOpenRef.current) {
          var _onIsOpenChangeRef$cu3;
          // eslint-disable-next-line react-hooks/exhaustive-deps
          (_onIsOpenChangeRef$cu3 = onIsOpenChangeRef.current) === null || _onIsOpenChangeRef$cu3 === void 0 || _onIsOpenChangeRef$cu3.call(onIsOpenChangeRef, false);
        }
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
    setSubmenuHighlightedIndex(-1);
    setSearchTerm('');
  }, [setHighlightedIndex, setIsOpen, setSearchTerm]);
  const handleMultiSelectChange = item => {
    let arr = selectedValue || selectedValue === 0 ? [].concat(selectedValue) : [];
    const i = arr.findIndex(v => {
      var _item$props;
      return v === (item === null || item === void 0 || (_item$props = item.props) === null || _item$props === void 0 ? void 0 : _item$props.value);
    });
    if (i !== -1) {
      arr.splice(i, 1);
    } else {
      var _item$props2;
      arr = union(arr, item === null || item === void 0 || (_item$props2 = item.props) === null || _item$props2 === void 0 ? void 0 : _item$props2.value);
    }
    setSelectedValue(arr, getLabelFromValue(children, arr), item);
  };
  const handleSubmenuOnSelectedValueChange = (value, label, item) => {
    if (!selectable) return;
    if (isMultiSelect) {
      handleMultiSelectChange(item);
      return;
    }
    setSelectedValue(value, getLabelFromValue(children, value), item);
  };
  const handleItemSelected = item => {
    var _item$props3;
    if (!selectable) {
      closeAndFocusAnchor();
      return;
    }
    const value = item === null || item === void 0 || (_item$props3 = item.props) === null || _item$props3 === void 0 ? void 0 : _item$props3.value;
    if (!value && value !== 0) {
      // eslint-disable-next-line no-console
      console.warn('<MenuItem> should have a "value" prop when used in a <SelectMenu>', item);
    }
    if (isMultiSelect) {
      handleMultiSelectChange(item);
      return;
    }
    setSelectedValue(value, getLabelFromValue(children, value), item);
    closeAndFocusAnchor();
  };
  const handleClickAway = useCallback(wrapEvent(onClickAway, closeMenu), []);
  const preventClickOutside = useClickOutsidePortals(handleClickAway, {
    disabled: isSubmenu
  });
  const handleAnchorClick = () => {
    if (isOpen) {
      setIsOpen(false);
    } else if (private_isClosedMenuOpenedOnAnchorClick) {
      setIsOpen(true);
      setHighlightedIndex(private_isFirstElementHighlightedOnUserActivatedOpen ? 0 : -1);
    }
  };
  const handleAnchorKeyDown = e => {
    if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
      e.preventDefault(); // prevent scroll
      setIsOpen(true);
      setHighlightedIndex(private_isFirstElementHighlightedOnUserActivatedOpen ? 0 : -1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault(); // prevent scroll
      const lastIndex = React__default.Children.count(children) - (searchable ? 0 : 1);
      setIsOpen(true);
      setHighlightedIndex(lastIndex);
    } else if (e.key === 'Tab') {
      closeMenu();
    }
  };
  let menuItemIndex = searchable ? 1 : 0;
  const generateMenuItems = () => React__default.Children.map(filterItems(isSearchFilteringDisabled ? null : searchTerm, React__default.Children.toArray(children)), child => {
    const actualIndex = menuItemIndex;
    if (isType(child, menuType)) {
      menuItemIndex += 1;
      return /*#__PURE__*/React__default.cloneElement(child, {
        isOpen: actualIndex === highlightedIndex,
        isHighlighted: actualIndex === highlightedIndex,
        isFocused: actualIndex === highlightedIndex && submenuHighlightedIndex === -1,
        placement: 'right-start',
        onKeyDown: e => {
          if (e.key === 'ArrowRight' || e.key === 'Enter' || e.key === ' ') {
            setSubmenuHighlightedIndex(0);
            e.preventDefault(); // prevent scroll
          }
        },
        onClick: () => {
          setSubmenuHighlightedIndex(0);
        },
        highlightedIndex: submenuHighlightedIndex,
        isMultiSelect,
        defaultValue,
        selectedValue,
        onHighlightedIndexChange: setSubmenuHighlightedIndex,
        onSelectedValueChange: handleSubmenuOnSelectedValueChange,
        isVirtualized
      });
    }
    if ((child === null || child === void 0 ? void 0 : child.type) === MenuItem) {
      var _child$props, _child$props2;
      menuItemIndex += 1;
      return /*#__PURE__*/React__default.cloneElement(child, {
        isHighlighted: actualIndex === highlightedIndex,
        isSelected: selectable && typeof selectedValue !== 'undefined' && Array.isArray(selectedValue) ? selectedValue.includes(child === null || child === void 0 || (_child$props = child.props) === null || _child$props === void 0 ? void 0 : _child$props.value) : (child === null || child === void 0 || (_child$props2 = child.props) === null || _child$props2 === void 0 ? void 0 : _child$props2.value) === selectedValue,
        onSelect: wrapEvent(child.props.onSelect, () => {
          handleItemSelected(child);
        }),
        isMultiSelect,
        isSelectable: selectable
      });
    }
    return child;
  });
  const anchorLabel = label !== null && label !== void 0 ? label : getLabelFromValue(children, selectedValue);
  const menuAnchorLabel = anchorLabel !== null && anchorLabel !== void 0 ? anchorLabel : defaultLabel;
  const announceToScreenReader = useScreenReaderAnnouncement('assertive');
  const ariaLabelledBy = (_externalAnchor$props3 = externalAnchor === null || externalAnchor === void 0 || (_externalAnchor$props4 = externalAnchor.props) === null || _externalAnchor$props4 === void 0 ? void 0 : _externalAnchor$props4['aria-labelledby']) !== null && _externalAnchor$props3 !== void 0 ? _externalAnchor$props3 : externalAriaLabelledBy ? selectable ? externalAriaLabelledBy : "".concat(externalAriaLabelledBy, " ").concat(anchorId) : undefined;
  const ariaLabel = (_externalAnchor$props5 = externalAnchor === null || externalAnchor === void 0 || (_externalAnchor$props6 = externalAnchor.props) === null || _externalAnchor$props6 === void 0 ? void 0 : _externalAnchor$props6['aria-label']) !== null && _externalAnchor$props5 !== void 0 ? _externalAnchor$props5 : externalAriaLabel && typeof menuAnchorLabel === 'string' ? "".concat(externalAriaLabel, " ").concat(menuAnchorLabel) : externalAriaLabel;
  const ariaExpanded = (externalAnchor === null || externalAnchor === void 0 ? void 0 : externalAnchor.props['aria-expanded']) !== undefined ? externalAnchor.props['aria-expanded'] : isOpen ? true : undefined;
  const actualAnchor = externalAnchor !== undefined ? (/*#__PURE__*/React__default.cloneElement(externalAnchor, {
    ref: mergeRefs(ref, anchorRef, externalAnchor.ref),
    tabIndex: (_externalAnchor$props7 = externalAnchor.props.tabIndex) !== null && _externalAnchor$props7 !== void 0 ? _externalAnchor$props7 : 0,
    role: (_externalAnchor$props8 = externalAnchor.props.role) !== null && _externalAnchor$props8 !== void 0 ? _externalAnchor$props8 : 'button',
    id: anchorId,
    'aria-controls': isOpen ? arrowNavId : undefined,
    'aria-expanded': ariaExpanded,
    'aria-haspopup': 'menu',
    'aria-disabled': (_externalAnchor$props9 = externalAnchor.props['aria-disabled']) !== null && _externalAnchor$props9 !== void 0 ? _externalAnchor$props9 : disabled,
    'aria-labelledby': ariaLabelledBy,
    'aria-label': ariaLabel,
    onMouseDown: preventClickOutside,
    onClick: wrapEvent(externalAnchor.props.onClick, handleAnchorClick),
    onKeyDown: wrapEvent(externalAnchor.props.onKeyDown, handleAnchorKeyDown)
  })) : isSubmenu ? /*#__PURE__*/React__default.createElement(SubmenuAnchor, _extends({
    ref: mergeRefs(ref, anchorRef),
    label: label,
    id: anchorId,
    "aria-controls": isOpen ? arrowNavId : undefined,
    "aria-expanded": isOpen ? true : undefined,
    onClick: onClick,
    onKeyDown: onKeyDown,
    disabled: disabled,
    className: className
  }, rest)) : /*#__PURE__*/React__default.createElement(MenuAnchor, _extends({}, rest, {
    id: anchorId,
    "aria-controls": isOpen ? arrowNavId : undefined,
    "aria-expanded": isOpen ? true : undefined,
    "aria-labelledby": ariaLabelledBy,
    "aria-label": ariaLabel,
    ref: mergeRefs(ref, anchorRef),
    disabled: disabled,
    label: menuAnchorLabel,
    onMouseDown: preventClickOutside,
    onClick: wrapEvent(onClick, handleAnchorClick),
    onKeyDown: wrapEvent(onKeyDown, handleAnchorKeyDown),
    isOpen: isOpen,
    className: classNames(className, {
      [menuStyles.selected]: anchorLabel
    }),
    role: selectable ? 'combobox' : undefined
  }));
  const closeAndFocusAnchor = useCallback(() => {
    closeMenu();
    anchorRef.current && anchorRef.current.focus();
    parentCloseAndFocusAnchor && parentCloseAndFocusAnchor();
  }, [closeMenu, parentCloseAndFocusAnchor]);
  const menuItems = unstable_isLoading ? [/*#__PURE__*/React__default.createElement(MenuSpinner, {
    key: "menu-loading"
  })] : generateMenuItems();
  let noResults = false;
  const listItems = menuItems || [];
  const popperIsOpen = !disabled && isOpen;
  if (!listItems.length) {
    noResults = true;
    listItems.push(/*#__PURE__*/React__default.createElement("div", {
      key: "no-list-items",
      className: classNames(menuStyles.menuItem, menuStyles.noMenuItems),
      role: "menuitem"
    }, noMenuItemsToDisplayText));
  }
  useDidUpdate(() => {
    if (popperIsOpen && noResults) {
      announceToScreenReader(noMenuItemsToDisplayText);
    }
  }, [noResults, popperIsOpen, announceToScreenReader, noMenuItemsToDisplayText]);
  const searchBoxRef = useRef();
  const arrowNavRef = useRef(null);
  const handleHighlightedIndexChange = useCallback(i => setHighlightedIndex(i), [setHighlightedIndex]);
  const isNavItem = useCallback(child => (child === null || child === void 0 ? void 0 : child.type) === MenuItem || (child === null || child === void 0 ? void 0 : child.type) === MenuSearchBox || isType(child, menuType), [menuType]);
  return /*#__PURE__*/React__default.createElement(MenuContextProvider, {
    value: {
      closeAndFocusAnchor,
      setIsOpen,
      isOpen,
      anchorRef,
      setHighlightedIndex
    }
  }, /*#__PURE__*/React__default.createElement(Popper, {
    open: !disabled && isOpen,
    placement: placement,
    portalTarget: portalTarget,
    anchor: actualAnchor,
    modifiers: [{
      name: 'offset',
      options: {
        offset: isSubmenu ? SUBMENU_POPPER_OFFSET : FLYOUT_POPPER_OFFSET
      }
    }],
    popper: /*#__PURE__*/React__default.createElement(ArrowNav, {
      id: arrowNavId,
      role: "menu",
      "aria-labelledby": anchorId,
      isNavItem: isNavItem,
      style: {
        zIndex,
        maxWidth: maxFlyoutWidth !== null && maxFlyoutWidth !== void 0 ? maxFlyoutWidth : undefined,
        width: maxFlyoutWidth !== null && maxFlyoutWidth !== void 0 ? maxFlyoutWidth : undefined
      },
      onMouseDown: preventClickOutside,
      className: classNames(menuStyles.contentPane, flyoutClassName),
      highlightedIndex: highlightedIndex,
      maxHeight: maxHeight,
      minWidth: isSubmenu ? undefined : Math.min(350, Math.max(160, anchorRef === null || anchorRef === void 0 || (_anchorRef$current = anchorRef.current) === null || _anchorRef$current === void 0 ? void 0 : _anchorRef$current.offsetWidth)),
      searchable: searchable,
      onHighlightedIndexChange: handleHighlightedIndexChange,
      searchTerm: searchTerm,
      setSearchTerm: setSearchTerm,
      onKeyDown: e => {
        if (e.key === 'Escape') {
          e.stopPropagation(); // Prevent Escape key event from bubbling up
          e.preventDefault(); // Prevent Safari from exiting full-screen mode
          if (isSubmenu) {
            // Only update the highlighted index for the current menu
            setHighlightedIndex(-1);
          } else {
            closeAndFocusAnchor();
          }
        } else if (e.key === 'Tab') {
          // For Tab, focus should be moved relative to anchor
          // (not menu container) so we focus anchor before browser moves focus
          closeAndFocusAnchor();
        } else if (e.key === 'ArrowLeft' && isSubmenu) {
          var _arrowNavRef$current;
          // Only update the highlighted index for the current menu (i.e. if the event bubbles up to a parent menu, don't update the highlighted index for the parent menu)
          if ((_arrowNavRef$current = arrowNavRef.current) !== null && _arrowNavRef$current !== void 0 && _arrowNavRef$current.contains(e.target) && e.target !== searchBoxRef.current) {
            var _anchorRef$current2;
            setHighlightedIndex(-1);
            (_anchorRef$current2 = anchorRef.current) === null || _anchorRef$current2 === void 0 || _anchorRef$current2.focus();
          }
        }
      },
      isVirtualized: isVirtualized,
      isSubmenu: isSubmenu,
      searchBoxRef: searchBoxRef,
      anchorRef: anchorRef,
      ref: arrowNavRef
    }, listItems)
  }));
});
function filterItems(valueToFilterBy, items) {
  if (!valueToFilterBy) {
    return items;
  }
  const result = items.filter(item => {
    const searchTermLowerCase = valueToFilterBy.toLowerCase();
    if (typeof item.props.label === 'string') {
      return item.props.label.toLowerCase().includes(searchTermLowerCase);
    }
    if (typeof item.props.children === 'string') {
      return item.props.children.toLowerCase().includes(searchTermLowerCase);
    }
    // eslint-disable-next-line no-console
    console.warn("<MenuItem> not included in search results!\n        Ensure prop \"children\" is of type \"string\" or provide a \"label\" prop!", item);
    return false;
  });
  return result;
}
function getLabelFromValue(children, value) {
  if (!value && value !== 0) return undefined;
  if (Array.isArray(value)) {
    if (value.length === 0) return undefined;
    return [].concat(value).map(v => getLabel(getDescendent(children, v))).filter(label => label || label === 0).join(', ');
  }
  return getLabel(getDescendent(children, value));
}

// Mega-hack to make nested defaultValue prop work
function getDescendent(children, value) {
  if (value === undefined) return null;
  const childArray = React__default.Children.toArray(children);
  for (let i = 0; i < childArray.length; i += 1) {
    var _child$props3, _child$props4;
    const child = childArray[i];
    if ((child === null || child === void 0 ? void 0 : child.type) === MenuItem && (child === null || child === void 0 || (_child$props3 = child.props) === null || _child$props3 === void 0 ? void 0 : _child$props3.value) === value) {
      return child;
    }
    if ((child === null || child === void 0 ? void 0 : child.type) !== MenuItem && child !== null && child !== void 0 && (_child$props4 = child.props) !== null && _child$props4 !== void 0 && _child$props4.children) {
      const descendent = getDescendent(child.props.children, value);
      if (descendent) {
        return descendent;
      }
    }
  }
  return null;
}
function getLabel(menuOptionNode) {
  var _menuOptionNode$props;
  if (/*#__PURE__*/isValidElement(menuOptionNode)) return getLabel(menuOptionNode === null || menuOptionNode === void 0 || (_menuOptionNode$props = menuOptionNode.props) === null || _menuOptionNode$props === void 0 ? void 0 : _menuOptionNode$props.children);
  return menuOptionNode;
}
function union(arr1, arr2) {
  return [].concat(arr1).concat(arr2).reduce((result, current) => {
    if (!result.includes(current)) {
      result.push(current);
    }
    return result;
  }, []);
}
function isType(element, type) {
  return element && element.type && element.type.render === type;
}

export { BaseMenu as default };
